import React from "react";
import { useState } from "react";
import { Box, Divider, FormControlLabel, Typography, useTheme } from "@mui/material";
import { OverviewDataCard, ScoringPill, TeamDataCard } from "./components";
import { Checkbox } from "components/Checkbox";
import { ICReportRestrictedView } from "pages/LeadershipDashboard/components/ICReportRestrictedView";

import { useAbility } from "@casl/react";
import { AbilityContext } from "context/Ability";

import { ICReportConverted } from "store/slice/Project/icDashboard/icDashboard.types";

interface LeadershipOverviewProps {
    data: ICReportConverted | null;
    companyName: string;
}

const LeadershipOverview = ({ data, companyName }: LeadershipOverviewProps) => {
    const theme = useTheme();
    const ability = useAbility(AbilityContext);
    const canSeeAbilityScores = ability.can("see", "LD_PROJECT_VIEW");

    const [isAllExpanded, setIsAllExpanded] = useState<boolean>(false);
    const [showDefinitions, setShowDefinitions] = useState<boolean>(false);

    const handleToggleAll = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsAllExpanded(event.target.checked);
    };

    const handleShowDefinitions = () => {
        setShowDefinitions(!showDefinitions);
    };

    return (
        <>
            {/* ABILITY SCORES */}
            <ScoringPill
                showDefinitions={showDefinitions}
                toggleDefinitions={handleShowDefinitions}
                disabled={!canSeeAbilityScores}
            />
            <Box>
                {/* OVERVIEW HEADER */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        p: 1,
                        borderRadius: 2,
                        m: 1,
                        backgroundColor:
                            data?.overview.ability_group_colour === "AMBER"
                                ? theme.palette.others.paleYellow
                                : data?.overview.ability_group_colour === "GREEN"
                                ? theme.palette.others.paleGreen
                                : theme.palette.others.paleRed,
                        height: 56,
                        boxShadow: "0 2px 4px 0 rgba(0, 0, 0, 0.2)",
                    }}
                >
                    <Typography variant="h5" fontWeight={600}>
                        {data?.overview.ability_group_label}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 600, fontStyle: "italic", fontSize: 30 }}>
                        {data?.overview.ability_group_score}
                    </Typography>
                </Box>

                {/* OVERVIEW CONTENT */}
                <Box sx={{ display: "flex", height: "calc(100vh - 310px)" }}>
                    <TeamDataCard
                        teamData={data?.leadershipTeam ?? null}
                        companyName={companyName}
                        disabled={!canSeeAbilityScores}
                    />

                    <Box px={1} sx={{ display: "flex", flexDirection: "column", flex: "1 1 auto", overflow: "hidden" }}>
                        <Box display="flex" alignItems="center">
                            <Typography variant="body2" mr={2} sx={{ color: "#343434" }}>
                                {data?.overview.ability_group_description}
                            </Typography>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isAllExpanded}
                                        onChange={handleToggleAll}
                                        disabled={!canSeeAbilityScores}
                                    />
                                }
                                label="Expand All"
                                sx={{
                                    "& .MuiFormControlLabel-label": {
                                        fontSize: 16,
                                        whiteSpace: "nowrap",
                                    },
                                }}
                            />
                        </Box>
                        <Box sx={{ flex: 1, overflowY: "auto" }}>
                            <Box display="flex" alignItems="flex-start" justifyContent="space-evenly" mt={2}>
                                {canSeeAbilityScores ? (
                                    <>
                                        {/* EXPANDABLE CARD */}
                                        {data?.overview.ability_subgroups.map((teamOverviewScore, index) => (
                                            <React.Fragment key={teamOverviewScore.subgroup_id}>
                                                <OverviewDataCard
                                                    data={teamOverviewScore}
                                                    isAllExpanded={isAllExpanded}
                                                    showDefinitions={showDefinitions}
                                                />

                                                {index < data.overview.ability_subgroups.length - 1 && (
                                                    <Divider
                                                        orientation="vertical"
                                                        flexItem
                                                        sx={{ marginLeft: 1, marginRight: 1 }}
                                                    />
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </>
                                ) : (
                                    <ICReportRestrictedView isBoard />
                                )}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </>
    );
};

export default LeadershipOverview;
